import DPO from 'dpo8core';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import endpoint from '@app/endpoint';
import { connect } from 'react-redux';
import { loadFlowConfig } from '../init';
import { logError } from '@app/error';
import useLogRocket from '@app/hooks/use-logrocket';
import useTrackerAir360 from '@app/hooks/use-tracker-Air360';
import useGoogleTagManager from '@app/hooks/use-gtm';
import { deepEqual } from '@app/router/utils';

const defaultContextState = {
  flowConfig: {},
  context: {},
  flow: {},
  locale: null,
};

export const QAUContext = React.createContext(defaultContextState);

export const inject = (...contextFields) => ComponentToWrap => {
  const componentName = ComponentToWrap.displayName || ComponentToWrap.name || 'Component';
  const InjectContext = props => (
    <QAUContext.Consumer>
      {context => {
        const newProps = {
          ...ComponentToWrap.props,
          ...props,
        };
        contextFields.forEach(key => (newProps[key] = context[key]));
        return <ComponentToWrap {...newProps} />;
      }}
    </QAUContext.Consumer>
  );
  InjectContext.displayName = `${componentName}-inject(${contextFields.join(', ')})`;
  return InjectContext;
};

const ContextProvider = ({ children, behavior, consumer, productId, ...props }) => {
  const [state, setState] = useState({ ...defaultContextState });
  const [flowConfigParams, setFlowConfigParams] = useState(null);

  const { current: constants } = useRef({
    APP_STATUS_READY: 1,
    APP_STATUS_ERROR: 2,
  });

  const getFlowConfigParams = useCallback(
    ({ productId, behavior, consumer }) => ({
      ...(productId && { productId }),
      ...(behavior && { behavior }),
      ...(consumer && { consumer }),
    }),
    [],
  );

  const onInitApp = useCallback(
    ({ flowConfig, layoutManager, colorSpace, converter }) => {
      const context = new DPO.bll.Context(null, layoutManager, null, null, null, endpoint);
      const flow = new DPO.bll.CustomizeFlow(endpoint, null, null, flowConfig);
      const locale = flowConfig?.locale?.name || 'en-US';

      context.layoutManager.on('fonts:loaded', () => {
        context.layoutManager.resetObservable();
        setState({
          flow,
          flowConfig,
          context,
          locale,
          appStatus: constants.APP_STATUS_READY,
        });
      });
      context.colorSpace = colorSpace;
      context.flowConfig = flowConfig;
      context.converter = converter;
      context.layoutManager.loadFontMap({});
    },
    [constants.APP_STATUS_READY],
  );

  const onError = useCallback(
    (errors = []) => {
      const [error] = Array.isArray(errors) ? errors : [errors];
      setState({
        appStatus: constants.APP_STATUS_ERROR,
      });
      logError(new Error(error.message));
    },
    [constants.APP_STATUS_ERROR],
  );

  const onFlowConfigLoaded = useCallback(data => {
    const appReady = canLaunch();
    if (!appReady) {
      onInitApp(data);
      return;
    }

    const { flowConfig, colorSpace, converter } = data;
    setState(state => {
      const { context, flow } = state;
      flow.flowConfig = flowConfig;
      context.colorSpace = colorSpace;
      context.flowConfig = flowConfig;
      context.converter = converter;
      context.flow = flow;

      return {
        ...state,
        flowConfig,
        context,
      };
    });
  }, [state.appStatus]);

  const contextUpdater = useCallback(
    context => {
      const { converter } = state.context;
      context.converter = converter;
      context.filter = new DPO.project.Filter();
      setState(state => ({
        ...state,
        context,
      }));
    },
    [state.context],
  );

  const getContext = useCallback(() => {
    const { context, flow, flowConfig, locale } = state;
    return {
      flow,
      locale,
      context,
      flowConfig,
      updateContext: contextUpdater,
    };
  }, [contextUpdater, state]);

  const canLaunch = useCallback(() => {
    return [constants.APP_STATUS_READY].includes(state.appStatus);
  }, [state.appStatus, constants.APP_STATUS_READY]);

  const updateFlowConfig = params => {
    const flowParams = getFlowConfigParams(params);
    if (deepEqual(flowConfigParams, flowParams)) return;

    setFlowConfigParams(flowParams);
    loadFlowConfig(flowParams).then(onFlowConfigLoaded).catch(onError);
  };

  useEffect(() => {
    updateFlowConfig({ behavior, consumer, productId });
  }, [behavior, consumer, productId, updateFlowConfig]);

  useEffect(() => {
    DPO.dal.Request.prototype.consumerId = consumer;
  }, [consumer]);

  useEffect(() => {
    DPO.dal.Request.prototype.behavior = behavior;
  }, [behavior]);

  useLogRocket(getContext());
  useTrackerAir360(getContext());
  useGoogleTagManager(getContext());

  return <QAUContext.Provider value={getContext()}>{canLaunch() && children}</QAUContext.Provider>;
};

const mapStateToProps = ({ matrix: { design, ctx } }) => ({
  productId: design.product,
  behavior: ctx.behavior,
  consumer: ctx.consumer,
});

ContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
  productId: PropTypes.string.isRequired,
  behavior: PropTypes.string,
  consumer: PropTypes.string,
};

export default connect(mapStateToProps)(ContextProvider);
